import { useState } from 'react'
import { ChevronDown, ChevronRight, ArrowRight, Target } from 'lucide-react'
import { scopeStatuses, kpis } from '../data'

export function ScopeKpiView() {
  return (
    <div className="space-y-8">
      {/* Scope Status Lifecycle */}
      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Scope Status Lifecycle</h3>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Every scope in DealHub progresses through these statuses. DealHub enforces 100% completion before a scope can be confirmed.
          </p>
        </div>

        {/* Status flow - horizontal */}
        <div className="flex flex-wrap items-center gap-2 p-4 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          {scopeStatuses.map((status, i) => (
            <div key={status.id} className="flex items-center gap-2">
              <div className={`px-3 py-1.5 rounded-lg ${status.bg} border border-[var(--border-color)]`}>
                <span className={`text-xs font-semibold ${status.color}`}>{status.label}</span>
              </div>
              {i < scopeStatuses.length - 1 && (
                <ArrowRight size={14} className="text-[var(--text-muted)] shrink-0" />
              )}
            </div>
          ))}
        </div>

        {/* Status details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {scopeStatuses.map((status) => (
            <div
              key={status.id}
              className={`p-3 rounded-lg ${status.bg} border border-[var(--border-color)]`}
            >
              <h4 className={`text-xs font-semibold ${status.color}`}>{status.label}</h4>
              <p className="text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
                {status.description}
              </p>
            </div>
          ))}
        </div>

        {/* Adjust loop note */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
          <span className="text-amber-400 text-xs mt-0.5">Note:</span>
          <p className="text-xs text-[var(--text-muted)]">
            The <strong className="text-amber-400">Sent → Adjust → Sent</strong> loop can repeat multiple times.
            Customer may send the scope back for adjustments as many times as needed before confirming.
          </p>
        </div>
      </section>

      {/* KPIs */}
      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Presales KPIs (Key Results)</h3>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Four key results tracked quarterly on the Performance tab of the Presales Dashboard.
          </p>
        </div>

        <div className="space-y-3">
          {kpis.map((kpi) => (
            <KpiCard key={kpi.id} kpi={kpi} />
          ))}
        </div>
      </section>
    </div>
  )
}

function KpiCard({ kpi }: { kpi: typeof kpis[number] }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Target size={14} className="text-[var(--accent-color)] shrink-0" />
              <h4 className="text-sm font-semibold text-[var(--text-primary)]">{kpi.label}</h4>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1">{kpi.metric}</p>
          </div>
          {expanded ? (
            <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0 mt-1" />
          ) : (
            <ChevronRight size={14} className="text-[var(--text-muted)] shrink-0 mt-1" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-[var(--border-color)] pt-3">
          <p className="text-xs text-[var(--text-secondary)]">{kpi.description}</p>

          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-[var(--accent-color)]/10">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--accent-color)] shrink-0 mt-0.5">
              Target
            </span>
            <span className="text-xs text-[var(--text-secondary)]">{kpi.target}</span>
          </div>

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
              How it's measured
            </p>
            <ul className="space-y-1">
              {kpi.how.map((item, i) => (
                <li key={i} className="text-xs text-[var(--text-muted)] flex gap-2">
                  <span className="shrink-0">-</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
